import type {
  Database,
  QueryCell,
  QueryPlan,
  QueryResult,
  SnapshotCommit,
  TableColumn,
  TableDetail,
  TableInfo,
  TableStatistics,
} from '@milliswift/types'

/**
 * Fixtures for the console endpoints the platform does not serve yet.
 *
 * These exist so the console UI is complete and reviewable today. They are
 * obviously synthetic — round numbers, a small fixed catalogue — precisely so
 * nobody mistakes them for live data, and every page that renders them also
 * shows <PreviewDataBadge />.
 *
 * Delete this directory when the endpoints land: docs/PLATFORM_API_GAPS.md.
 */

const NOW = Date.now()
const iso = (daysAgo: number, hoursAgo = 0) =>
  new Date(NOW - daysAgo * 86_400_000 - hoursAgo * 3_600_000).toISOString()

export const MOCK_DATABASES: Database[] = [
  {
    database_id: 'db_9f2c4a1e8b7d',
    name: 'analytics',
    region: 'us-east-1',
    status: 'active',
    row_count: 1_501_388_105,
    size_bytes: 223_800_000_000,
    created_at: iso(180),
  },
  {
    database_id: 'db_3a8e1c5f2d90',
    name: 'warehouse',
    region: 'eu-west-1',
    status: 'active',
    row_count: 894_220_441,
    size_bytes: 141_200_000_000,
    created_at: iso(95),
  },
  {
    database_id: 'db_7c4b9d0a6e13',
    name: 'staging',
    region: 'us-east-1',
    status: 'provisioning',
    row_count: 0,
    size_bytes: 0,
    created_at: iso(0, 1),
  },
]

export const MOCK_TABLES: TableInfo[] = [
  {
    table_name: 'events_raw',
    row_count: 1_204_889_301,
    size_bytes: 193_800_000_000,
    residency: 'cold',
    column_count: 14,
    last_query_at: iso(2),
  },
  {
    table_name: 'orders',
    row_count: 284_099_112,
    size_bytes: 44_200_000_000,
    residency: 'hot',
    column_count: 11,
    last_query_at: iso(0, 1),
  },
  {
    table_name: 'users',
    row_count: 12_400_881,
    size_bytes: 2_250_000_000,
    residency: 'hot',
    column_count: 9,
    last_query_at: iso(0, 3),
  },
  {
    table_name: 'products',
    row_count: 88_412,
    size_bytes: 104_000_000,
    residency: 'warm',
    column_count: 12,
    last_query_at: iso(4),
  },
]

const ORDERS_COLUMNS: TableColumn[] = [
  { name: 'id', data_type: 'INT8', nullable: false, default_value: null, comment: 'Primary key' },
  { name: 'user_id', data_type: 'INT8', nullable: false, default_value: null, comment: 'FK to users.id' },
  { name: 'status', data_type: 'TEXT', nullable: false, default_value: "'pending'", comment: 'Order lifecycle state' },
  { name: 'total_amount', data_type: 'FLOAT8', nullable: true, default_value: null, comment: null },
  { name: 'currency', data_type: 'TEXT', nullable: false, default_value: "'USD'", comment: 'ISO 4217' },
  { name: 'created_at', data_type: 'TIMESTAMP', nullable: false, default_value: 'now()', comment: null },
  { name: 'shipped_at', data_type: 'TIMESTAMP', nullable: true, default_value: null, comment: null },
]

const ORDERS_STATS: TableStatistics[] = [
  { column: 'status', distinct_count: 6, null_count: 0, min: 'cancelled', max: 'shipped', average: null },
  { column: 'total_amount', distinct_count: 1_884_201, null_count: 4_182, min: '0.99', max: '148_220.00', average: 184.42 },
  { column: 'created_at', distinct_count: 284_099_112, null_count: 0, min: iso(900), max: iso(0), average: null },
]

export function mockTableDetail(tableName: string): TableDetail {
  const table = MOCK_TABLES.find((t) => t.table_name === tableName) ?? MOCK_TABLES[1]!

  return {
    table,
    columns: ORDERS_COLUMNS,
    indexes: [
      { name: 'orders_pkey', columns: ['id'], unique: true },
      { name: 'orders_user_id_idx', columns: ['user_id'], unique: false },
      { name: 'orders_created_at_idx', columns: ['created_at'], unique: false },
    ],
    statistics: ORDERS_STATS,
  }
}

/* --------------------------------------------------------------- queries */

const SAMPLE_ROWS: Array<Array<QueryCell>> = [
  ['shipped', 184_220, '2026-09-01'],
  ['pending', 42_118, '2026-09-02'],
  ['cancelled', 12_004, '2026-09-03'],
  ['delivered', 201_884, '2026-09-04'],
  ['shipped', 166_412, '2026-09-05'],
  ['pending', 51_220, '2026-09-06'],
  ['delivered', 188_004, '2026-09-07'],
  ['refunded', 3_412, '2026-09-08'],
]

const SAMPLE_PLAN: QueryPlan = {
  total_ms: 23,
  rows_processed: 284_750_000,
  memory_mb: 184,
  steps: [
    { step: 'Scan', detail: 'orders (3 segments pruned of 48)', duration_ms: 8, rows: 284_750_000 },
    { step: 'Filter', detail: "status = 'shipped'", duration_ms: 2, rows: 184_220 },
    { step: 'Aggregate', detail: 'GROUP BY status', duration_ms: 9, rows: 6 },
    { step: 'Projection', detail: '4 columns', duration_ms: 4, rows: 6 },
  ],
}

export function mockQueryResult(sql: string): QueryResult {
  const looksLikeSelect = /^\s*(select|with)/i.test(sql)

  if (!looksLikeSelect) {
    return {
      columns: [],
      rows: [],
      row_count: 0,
      duration_ms: 12,
      truncated: false,
    }
  }

  return {
    columns: [
      { name: 'status', type: 'TEXT' },
      { name: 'total', type: 'INT8' },
      { name: 'day', type: 'TIMESTAMP' },
    ],
    rows: SAMPLE_ROWS,
    row_count: SAMPLE_ROWS.length,
    duration_ms: 23,
    truncated: false,
    plan: SAMPLE_PLAN,
  }
}

/* ------------------------------------------------------------- snapshots */

export const MOCK_SNAPSHOTS: SnapshotCommit[] = [
  {
    commit_hash: 'a1b2c3d',
    created_at: iso(1),
    size_delta_bytes: 2_470_000_000,
    table_count: 4,
    tables: [
      { table_name: 'orders', size_bytes: 44_200_000_000 },
      { table_name: 'users', size_bytes: 2_250_000_000 },
      { table_name: 'products', size_bytes: 104_000_000 },
      { table_name: 'events_raw', size_bytes: 193_800_000_000 },
    ],
  },
  {
    commit_hash: 'f4e5d6c',
    created_at: iso(2),
    size_delta_bytes: 858_993_459,
    table_count: 4,
    tables: [
      { table_name: 'orders', size_bytes: 43_900_000_000 },
      { table_name: 'users', size_bytes: 2_180_000_000 },
      { table_name: 'products', size_bytes: 104_000_000 },
      { table_name: 'events_raw', size_bytes: 191_200_000_000 },
    ],
  },
  {
    commit_hash: '9g8h7i6',
    created_at: iso(3),
    size_delta_bytes: 5_126_881_204,
    table_count: 4,
    tables: [
      { table_name: 'orders', size_bytes: 43_100_000_000 },
      { table_name: 'users', size_bytes: 2_010_000_000 },
      { table_name: 'products', size_bytes: 103_000_000 },
      { table_name: 'events_raw', size_bytes: 184_400_000_000 },
    ],
  },
]

export const MOCK_RESTORE_PROGRESS = {
  steps: [
    { label: 'Pause query engine', status: 'done' as const },
    { label: 'Restore snapshot', status: 'running' as const },
    { label: 'Resume query engine', status: 'pending' as const },
    { label: 'Validate integrity', status: 'pending' as const },
  ],
  eta_seconds: 154,
}
